import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import { Security, Warning } from '@mui/icons-material';

interface TwoFactorVerificationProps {
  open: boolean;
  onVerify: (code: string) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string;
}

const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({
  open,
  onVerify,
  onCancel,
  isLoading = false,
  error: externalError,
}) => {
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [localError, setLocalError] = useState('');

  const error = externalError || localError;

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Guard against double-submit: the Verify button is disabled while a verification is in flight,
    // but the form's onSubmit still fires on Enter (which bypasses the disabled button). Re-check
    // isLoading here so a fast second Enter cannot dispatch onVerify twice.
    if (isLoading) {
      return;
    }

    if (!code) {
      setLocalError('Please enter a verification code');
      return;
    }

    if (!useBackupCode && code.length !== 6) {
      setLocalError('Verification code must be 6 digits');
      return;
    }

    if (useBackupCode && code.length !== 9) {
      setLocalError('Backup code must be 9 digits');
      return;
    }

    setLocalError('');
    await onVerify(code);
  };

  const handleCodeChange = (value: string) => {
    const sanitized = value.replace(/\D/g, '');
    const maxLength = useBackupCode ? 9 : 6;
    setCode(sanitized.slice(0, maxLength));
    setLocalError('');
  };

  const toggleBackupCode = () => {
    setUseBackupCode(!useBackupCode);
    setCode('');
    setLocalError('');
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: handleSubmit,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Security color="primary" />
          <Typography variant="h6">Two-Factor Authentication</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {!useBackupCode ? (
            <>
              <Typography variant="body1" gutterBottom>
                Enter the 6-digit code from your authenticator app:
              </Typography>
              <TextField
                fullWidth
                autoFocus
                label="Verification Code"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="000000"
                disabled={isLoading}
                sx={{ mt: 2, mb: 2 }}
                slotProps={{
                  htmlInput: {
                    maxLength: 6,
                    pattern: '[0-9]*',
                    inputMode: 'numeric',
                    style: { 
                      fontSize: '1.5rem', 
                      letterSpacing: '0.5rem', 
                      textAlign: 'center' 
                    },
                  }
                }}
              />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Open your authenticator app to view your code
              </Typography>
            </>
          ) : (
            <>
              <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Using a backup code will permanently invalidate it. Make sure to save any
                  remaining codes.
                </Typography>
              </Alert>
              <Typography variant="body1" gutterBottom>
                Enter one of your backup codes:
              </Typography>
              <TextField
                fullWidth
                autoFocus
                label="Backup Code"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="000000000"
                disabled={isLoading}
                sx={{ mt: 2, mb: 2 }}
                slotProps={{
                  htmlInput: {
                    maxLength: 9,
                    pattern: '[0-9]*',
                    inputMode: 'numeric',
                    style: { 
                      fontSize: '1.25rem', 
                      letterSpacing: '0.25rem', 
                      textAlign: 'center',
                      fontFamily: 'monospace',
                    },
                  }
                }}
              />
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" mt={2}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={toggleBackupCode}
              underline="hover"
              sx={{ cursor: 'pointer' }}
            >
              {useBackupCode
                ? 'Use authenticator app instead'
                : "Lost your device? Use a backup code"}
            </Link>
          </Box>

          {isLoading && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={
            isLoading ||
            (!useBackupCode && code.length !== 6) ||
            (useBackupCode && code.length !== 9)
          }
        >
          Verify
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorVerification;